import React from "react";
import { View, StyleSheet, ScrollView, Platform, type StyleProp, type ViewStyle } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../../ThemedText";
import { ThemedButton } from "../../ThemedButton";
import { ThemedToolTip } from "../../ThemedToolTip";
import { useThemeColor } from "@/hooks/useThemeColor";

export interface ITableToolbarProps {
  visible: boolean;
  position?: { top: number; left: number };
  onAddRow: () => void;
  onDeleteRow: () => void;
  onAddColumn: () => void;
  onDeleteColumn: () => void;
  onDeleteTable: () => void;
  onClose?: () => void;
  isStandalonePreview?: boolean;
  style?: StyleProp<ViewStyle>;
}

/**
 * TableToolbar (formerly TableFormatToolbar)
 *
 * Floating popup toolbar for interactive table operations.
 * Features:
 * - Width form-fits only as wide as the toolbar buttons (`width: "auto"`).
 * - Standardized entirely with ThemedButton across all actions.
 * - Prevents mousedown default on web to preserve active selection.
 * - Positioned centered below the table content.
 */
export function TableToolbar({
  visible,
  position,
  onAddRow,
  onDeleteRow,
  onAddColumn,
  onDeleteColumn,
  onDeleteTable,
  onClose,
  isStandalonePreview = false,
  style,
}: ITableToolbarProps) {
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");

  if (!visible) return null;

  const containerStyle = isStandalonePreview
    ? [
        styles.container,
        {
          backgroundColor: cardBg,
          borderColor,
          position: "relative" as const,
          top: 0,
          left: 0,
        },
        style,
      ]
    : [
        styles.container,
        {
          backgroundColor: cardBg,
          borderColor,
          position: "absolute" as const,
          zIndex: 1000,
          top: position ? Math.max(8, position.top) : 8,
          left: position ? Math.max(8, position.left) : 16,
        },
        style,
      ];

  return (
    <View
      style={containerStyle}
      testID="table-toolbar"
      {...(Platform.OS === "web"
        ? ({
            onMouseDown: (e: any) => e.preventDefault(),
          } as any)
        : {})}
    >
      <View style={styles.controlsRow}>
        <ThemedText style={[styles.badge, { color: textMuted }]}>
          Table
        </ThemedText>

        <View style={[styles.divider, { backgroundColor: borderColor }]} />

        {/* Row actions */}
        <ThemedToolTip text="Add Row Below">
          <ThemedButton
            title="+ Row"
            variant="minimal"
            style={styles.actionBtn}
            textStyle={styles.btnLabel}
            onPress={onAddRow}
            accessibilityLabel="Add Row"
          />
        </ThemedToolTip>

        <ThemedToolTip text="Remove Row">
          <ThemedButton
            title="- Row"
            variant="minimal"
            style={styles.actionBtn}
            textStyle={styles.btnLabel}
            onPress={onDeleteRow}
            accessibilityLabel="Delete Row"
          />
        </ThemedToolTip>

        <View style={[styles.divider, { backgroundColor: borderColor }]} />

        {/* Column actions */}
        <ThemedToolTip text="Add Column Right">
          <ThemedButton
            title="+ Col"
            variant="minimal"
            style={styles.actionBtn}
            textStyle={styles.btnLabel}
            onPress={onAddColumn}
            accessibilityLabel="Add Column"
          />
        </ThemedToolTip>

        <ThemedToolTip text="Remove Column">
          <ThemedButton
            title="- Col"
            variant="minimal"
            style={styles.actionBtn}
            textStyle={styles.btnLabel}
            onPress={onDeleteColumn}
            accessibilityLabel="Delete Column"
          />
        </ThemedToolTip>

        <View style={[styles.divider, { backgroundColor: borderColor }]} />

        {/* Delete table */}
        <ThemedToolTip text="Delete Entire Table">
          <ThemedButton
            title=""
            icon={<Ionicons name="trash-outline" size={13} color="#DC2626" />}
            variant="minimal"
            style={[styles.deleteBtn, { backgroundColor: "#FEE2E2", borderColor: "#EF4444" }]}
            onPress={onDeleteTable}
            accessibilityLabel="Delete Table"
          />
        </ThemedToolTip>

        {onClose && (
          <ThemedButton
            title=""
            icon={<Ionicons name="close" size={13} color={textMuted} />}
            variant="minimal"
            style={styles.actionBtn}
            onPress={onClose}
            accessibilityLabel="Close Toolbar"
          />
        )}
      </View>
    </View>
  );
}

// Aliased export for backwards compatibility
export const TableFormatToolbar = TableToolbar;

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.16,
    shadowRadius: 8,
    elevation: 8,
    width: "auto",
    alignSelf: "center",
    maxWidth: "96%",
  },
  controlsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  badge: {
    fontSize: 10,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginRight: 2,
  },
  divider: {
    width: 1,
    height: 16,
    marginHorizontal: 2,
  },
  actionBtn: {
    height: 24,
    minWidth: 24,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  btnLabel: {
    fontSize: 11,
    fontWeight: "600",
  },
  deleteBtn: {
    height: 24,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
});
